import { FormArray, FormControl, UntypedFormGroup } from '@angular/forms';
import { I18NextService } from 'angular-i18next';
import { EnumHelper } from 'mbs-ui-kit';
import { FullBackupData, IncrementalData, syntheticFullDefaults } from 'mbs-ui-shared@models/backup/schedule-models';
import moment from 'moment/moment';
import { DayOfWeek } from 'wizards/models/DayOfWeek.enum';
import { WeekNumber } from 'wizards/models/WeekNumber.enum';

const defaultIncrementalWeekly = [false, true, true, true, true, true, false];
const defaultFullWeekly = [true, false, false, false, false, false, false];

const getSchedule = (isIncremental: boolean) => {
  const commonForm = {
    recurringPeriod: new FormControl<'Daily' | 'Monthly'>(isIncremental ? 'Daily' : 'Monthly'),
    dailyFreqPeriodOption: new FormControl<'OccursAt' | 'OccursEvery'>('OccursAt'),
    dayOfWeek: new FormControl<number>(3),
    dayOfMonthCount: new FormControl<number>(1),
    occurrence: new FormControl<number>(0),
    occursAtTime: new FormControl<string>('00:00'),
    weeklyDayOfWeek: new FormArray((isIncremental ? defaultIncrementalWeekly : defaultFullWeekly).map((v) => new FormControl<boolean>(v))),
    repeatEveryCount: new FormControl<number>(1),
    startFromDate: new FormControl<Date>(new Date())
  };
  const incrementalForm = {
    enabled: new FormControl<boolean>(true),
    occursEveryCount: new FormControl<number>(1),
    occursEveryPeriod: new FormControl<'hours' | 'minutes'>('hours'),
    occursEveryFromTime: new FormControl<string>('09:00'),
    occursEveryToTime: new FormControl<string>('21:00')
  };
  const fullForm = {
    enabled: new FormControl<boolean>(false)
  };

  return isIncremental ? { ...commonForm, ...incrementalForm } : { ...commonForm, ...fullForm };
};

const getSyntheticSchedule = () => ({
  recurringPeriod: new FormControl<'Weekly' | 'Monthly'>(syntheticFullDefaults.recurringPeriod),
  dayOfWeek: new FormControl<number>(syntheticFullDefaults.dayOfWeek),
  occurrence: new FormControl<number>(syntheticFullDefaults.occurrence),
  runMissed: new FormControl<boolean>(syntheticFullDefaults.runMissed)
});

const isSelectedDaysValidator = (formGroup: UntypedFormGroup, isNBF: boolean, isRestore: boolean) => {
  const isFFI = formGroup.value.ScheduleType === 'ffi';
  const isPredefined = formGroup.value.ScheduleType === 'predefined';

  if (isNBF && !isRestore && (isFFI || isPredefined)) {
    const incremental = formGroup.get('incrementalData') as UntypedFormGroup;
    const fullData = formGroup.get('fullBackupData') as UntypedFormGroup;
    const incErr = incremental.get('recurringPeriod').value === 'Daily' && !incremental.get('weeklyDayOfWeek').value.some((day) => !!day);
    const fullErr =
      fullData.get('enabled').value &&
      fullData.get('recurringPeriod').value === 'Daily' &&
      !fullData.get('weeklyDayOfWeek').value.some((day) => !!day);

    if (incErr) {
      return { needSelectDay: { message: isFFI ? 'Forever Forward Incremental' : 'recurring (incremental)', isFFI } };
    }

    if (fullErr) {
      return { needSelectDay: { message: 'Execute full backup (Synthetic full is possible)' } };
    }

    return null;
  }

  return null;
};

// TODO: Test
export { isSelectedDaysValidator };
export const getDefaultIncrementalSchedule = () => getSchedule(true);
export const getDefaultFullSchedule = () => getSchedule(false);
export const getDefaultSyntheticSchedule = () => getSyntheticSchedule();
export const daysOfWeekEnumArray = EnumHelper.EnumToSelectIndexesArray(DayOfWeek);
export const weekNumberEnumArray = EnumHelper.EnumToSelectIndexesArray(WeekNumber).sort((a) => (a.label === WeekNumber[-1] ? -1 : 0));

type ScheduleTextTranslator = Pick<I18NextService, 't'>;

const getDailyInfo = (value: IncrementalData | FullBackupData, i18n: ScheduleTextTranslator): string => {
  const daysShort = i18n.t('shared:daysShort', { returnObjects: true });
  const weekDays = value.weeklyDayOfWeek
    .map((value, i) => (value ? daysShort[i].toUpperCase() : ''))
    .filter((value) => !!value)
    .join(', ');
  const period = value.recurringPeriod;
  const repeatEvery = value.repeatEveryCount;
  const repeatEveryMeasure = i18n.t('wizards:schedule.scheduleModal.repeat.weeks');

  return `${period} on: ${weekDays}. ${getFreqInfo(value)}. Repeat every: ${repeatEvery} ${repeatEveryMeasure}`;
};

const getMonthlyInfo = (value: IncrementalData | FullBackupData, i18n: ScheduleTextTranslator): string => {
  const period = value.recurringPeriod;
  const occurrence = value.occurrence;
  const occurrenceText = (weekNumberEnumArray.find((item) => item.value === occurrence) || { label: '' }).label;
  const isDayOfMonth = occurrence === WeekNumber['Day Of Month'];
  const repeatEvery = value.repeatEveryCount;
  const repeatEveryMeasure = i18n.t('wizards:schedule.scheduleModal.repeat.months');

  if (isDayOfMonth) {
    const dayOfMonth = value.dayOfMonthCount;

    return `${period} on: ${dayOfMonth} ${String(occurrenceText).toLowerCase()}. ${getFreqInfo(
      value
    )}. Repeat every: ${repeatEvery} ${repeatEveryMeasure}`;
  }

  const dayOfWeek = value.dayOfWeek;
  const dayOfWeekText = (daysOfWeekEnumArray.find((item) => item.value === dayOfWeek) || { label: '' }).label;

  return `${period} on: ${String(occurrenceText).toLowerCase()} ${dayOfWeekText}. ${getFreqInfo(
    value
  )}. Repeat every: ${repeatEvery} ${repeatEveryMeasure}`;
};

const getFreqInfo = (value: IncrementalData | FullBackupData): string => {
  const isOccursAt = value.dailyFreqPeriodOption === 'OccursAt';
  const occursAtTime = moment(value.occursAtTime, 'HH:mm').format('h:mm\u00A0A');
  const occursEveryCount = (value as IncrementalData).occursEveryCount;
  const occursEveryPeriod = (value as IncrementalData).occursEveryPeriod;
  const occursEveryFromTime = moment((value as IncrementalData).occursEveryFromTime, 'HH:mm').format('h:mm\u00A0A');
  const occursEveryToTime = moment((value as IncrementalData).occursEveryToTime, 'HH:mm').format('h:mm\u00A0A');

  return isOccursAt
    ? `Starts at ${occursAtTime}`
    : `Starts every ${occursEveryCount} ${occursEveryPeriod} from ${occursEveryFromTime} to ${occursEveryToTime}`;
};

export const getScheduleDataInfo = (value: IncrementalData | FullBackupData, i18n: ScheduleTextTranslator): string =>
  value.recurringPeriod === 'Daily' ? getDailyInfo(value, i18n) : getMonthlyInfo(value, i18n);

export const getAdvancedScheduleDataInfo = (
  value: IncrementalData | FullBackupData,
  type: 'incrementalData' | 'fullBackupData',
  i18n: ScheduleTextTranslator
): string => {
  if (!value.enabled) {
    return i18n.t(
      type === 'fullBackupData' ? 'wizards:schedule.advancedMode.fullDisabled' : 'wizards:schedule.advancedMode.incrementalDisabled'
    );
  }

  return getScheduleDataInfo(value, i18n);
};
